import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api, { getImageUrl } from '../config/api';
import moment from 'moment';
import PageNavbar from './PageNavbar';
import { AuthContext } from '../context/AuthContext';
import './CreateOrder.css';

// Normalize to YYYY-MM-DD so date input and API always use same calendar date (no timezone shift)
const toDateOnly = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  return moment(d).isValid() ? moment(d).format('YYYY-MM-DD') : '';
};

const parseAddress = (customerAddress) => {
  if (!customerAddress || !customerAddress.trim()) {
    return { line1: '', line2: '', city: '', state: 'Telangana', pincode: '500001' };
  }
  const parts = customerAddress.split('\n').map(s => s.trim()).filter(Boolean);
  if (parts.length >= 4) {
    const city = parts[2];
    const state = parts[3] || 'Telangana';
    const isCityOther = city && !['Hyderabad', 'Secunderabad', 'Other'].includes(city);
    const pincode = parts.length >= 5 && /^50\d{4}$/.test(parts[4]) && parseInt(parts[4], 10) >= 500001 && parseInt(parts[4], 10) <= 500100 ? parts[4] : '500001';
    return { line1: parts[0], line2: parts[1], city: isCityOther ? 'Other' : (city || 'Hyderabad'), city_other: isCityOther ? city : '', state: state, pincode };
  }
  if (parts.length === 3) {
    const city = parts[2];
    const isCityOther = city && !['Hyderabad', 'Secunderabad', 'Other'].includes(city);
    return { line1: parts[0], line2: parts[1], city: isCityOther ? 'Other' : (city || 'Hyderabad'), city_other: isCityOther ? city : '', state: 'Telangana', pincode: '500001' };
  }
  if (parts.length === 2) {
    return { line1: parts[0], line2: parts[1], city: 'Hyderabad', city_other: '', state: 'Telangana', pincode: '500001' };
  }
  return { line1: parts[0] || '', line2: '', city: 'Hyderabad', city_other: '', state: 'Telangana', pincode: '500001' };
};

const CreateOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editIdFromUrl = searchParams.get('edit');
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editIdFromUrl);
  const [agents, setAgents] = useState([]);
  const [caterers, setCaterers] = useState([]);
  const [grazingCatalog, setGrazingCatalog] = useState([]);
  const [grazingPickCategoryId, setGrazingPickCategoryId] = useState('');
  const [grazingDraft, setGrazingDraft] = useState({ itemId: '', quantity: 1, unit_price: '' });
  const [showEventDesserts, setShowEventDesserts] = useState(false);
  const [showEventGrazing, setShowEventGrazing] = useState(false);
  const [grazingTableTotalInput, setGrazingTableTotalInput] = useState('');
  const [desserts, setDesserts] = useState([]);
  const [cupCakes, setCupCakes] = useState([]);
  const [plants, setPlants] = useState([]);
  const [cakeFlavours, setCakeFlavours] = useState([]);
  const [orderCategories, setOrderCategories] = useState(['Online Orders', 'Events']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [formData, setFormData] = useState({
    category: 'Online Orders',
    customer_name: '',
    customer_address: '',
    address_line1: '',
    address_line2: '',
    city: 'Hyderabad',
    city_other: '',
    state: 'Telangana',
    pincode: '500001',
    customer_phone: '',
    delivery_date: moment().format('YYYY-MM-DD'),
    delivery_time: '10:00',
    agent_id: '',
    delivery_charge: 0,
    // Optional advance payment for whole order (Online / Store-1 / Store-2 only)
    advance_payment: '',
    caterer_id: '',
    caterer_select: '',
    party_name: '',
    event_date: '',
    event_venue: '',
    event_time: '',
    travel_expense: '',
    order_kind: 'standard',
    items: []
  });

  const ADDRESS_STATE_OPTIONS = ['Telangana'];
  const ADDRESS_CITY_OPTIONS = ['Hyderabad', 'Secunderabad', 'Other'];
  const PINCODE_OPTIONS = Array.from({ length: 100 }, (_, i) => String(500001 + i));

  const populateFormFromOrder = useCallback((order) => {
    const addr = parseAddress(order.customer_address);
    const items = (order.items || []).map(item => {
      let processedItem = { ...item };
      if (item.item_type === 'Cakes') {
        if (item.files && item.files.length > 0) {
          const cakeFiles = item.files.filter(f => f.file_type === 'cake_image');
          processedItem.cake_images = cakeFiles.map(f => ({ id: f.id, file_path: f.file_path, file_name: f.file_name }));
        } else {
          processedItem.cake_images = [];
        }
        processedItem.cake_details = {
          product_name: item.product_name || '',
          flavour: item.flavour || '',
          weight_kg: item.weight_kg != null ? item.weight_kg : 1,
          weight_decimal: item.weight_decimal != null ? item.weight_decimal : 0,
          price: item.cake_price || 0,
          message_on_cake: item.message_on_cake || '',
          message_on_card: item.message_on_card || '',
          message_on_base: item.message_on_base || '',
          special_instructions: item.cake_instructions || ''
        };
      }
      if (item.item_type === 'Other Items' || item.item_type === 'Grazing Table') {
        processedItem.custom_item_name = item.custom_item_name || '';
      }
      if (item.item_type === 'Desserts') {
        processedItem.billed_as = item.billed_as || 'per_piece';
      }
      if (item.item_type === 'Grazing Table') {
        processedItem.grazing_item_id = item.grazing_item_id || null;
      }
      if (item.item_type === 'Bouquets') {
        if (item.files && item.files.length > 0) {
          const bouquetFiles = item.files.filter(f => f.file_type === 'bouquet_reference');
          processedItem.bouquet_images = bouquetFiles.map(f => ({ id: f.id, file_path: f.file_path, file_name: f.file_name }));
        } else {
          processedItem.bouquet_images = [];
        }
      }
      if (item.item_type === 'Catering') {
        if (item.files && item.files.length > 0) {
          const cateringFiles = item.files.filter(f => f.file_type === 'catering_pdf');
          processedItem.catering_files = cateringFiles.map(f => ({ id: f.id, file_path: f.file_path, file_name: f.file_name }));
        } else {
          processedItem.catering_files = [];
        }
      }
      if (item.item_type === 'Plants') {
        if (item.files && item.files.length > 0) {
          const plantFiles = item.files.filter(f => f.file_type === 'plant_reference');
          processedItem.plant_images = plantFiles.map(f => ({ id: f.id, file_path: f.file_path, file_name: f.file_name }));
        } else {
          processedItem.plant_images = [];
        }
      }
      return processedItem;
    });
    setFormData({
      category: order.category,
      customer_name: order.customer_name,
      customer_address: order.customer_address || '',
      address_line1: addr.line1,
      address_line2: addr.line2,
      city: addr.city || 'Hyderabad',
      city_other: addr.city_other || '',
      state: addr.state || 'Telangana',
      pincode: addr.pincode || '500001',
      customer_phone: order.customer_phone || '',
      delivery_date: toDateOnly(order.delivery_date),
      delivery_time: order.delivery_time,
      agent_id: order.agent_id || '',
      delivery_charge: order.delivery_charge || 0,
      // Prefill booking advance (prefer advance payment rows; fallback to paid_amount)
      advance_payment: (() => {
        const advances = (order.payments || []).filter((p) =>
          String(p.notes || '').toLowerCase().includes('advance received')
        );
        if (advances.length) {
          const sum = advances.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
          return sum > 0 ? String(sum) : '';
        }
        return order.paid_amount != null && Number(order.paid_amount) > 0 ? String(order.paid_amount) : '';
      })(),
      caterer_id: order.caterer_id || '',
      caterer_select: order.caterer_id
        ? String(order.caterer_id)
        : (order.party_name ? '__other__' : ''),
      party_name: order.party_name || '',
      event_date: toDateOnly(order.event_date) || '',
      event_venue: order.event_venue || '',
      event_time: order.event_time || '',
      travel_expense: order.travel_expense != null && Number(order.travel_expense) !== 0
        ? String(order.travel_expense)
        : '',
      order_kind:
        order.order_kind === 'grazing' || order.order_kind === 'mixed'
          ? order.order_kind
          : 'standard',
      items
    });
    const hasDessert = items.some((it) => it.item_type === 'Desserts');
    const hasGrazing = items.some((it) => it.item_type === 'Grazing Table');
    setShowEventDesserts(hasDessert);
    setShowEventGrazing(hasGrazing);
    if (order.category === 'N Caterers' || order.category === 'Caterers') {
      setOrderCategories((prev) =>
        prev.includes(order.category) ? prev : [...prev, order.category]
      );
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchCaterers();
    fetchGrazingCatalog();
    fetchDesserts();
    fetchCupCakes();
    fetchPlants();
    fetchFlavours();
    api.get('/orders/categories').then((r) => {
      if (r.data?.categories?.length) {
        const cats = r.data.categories.filter((c) => c !== 'N Caterers');
        setOrderCategories((prev) => {
          const keepLegacy = prev.filter((c) => c === 'N Caterers' || c === 'Caterers');
          return [...cats, ...keepLegacy.filter((c) => !cats.includes(c))];
        });
        setFormData((prev) => {
          const allowed = cats.includes(prev.category) || prev.category === 'N Caterers' || prev.category === 'Caterers';
          if (!allowed && cats.length) {
            return { ...prev, category: cats[0] };
          }
          return prev;
        });
      }
    }).catch(() => {});

    const isEditFromUrl = editIdFromUrl && editIdFromUrl.trim() !== '';

    if (isEditFromUrl) {
      const orderId = editIdFromUrl.trim();
      setIsEditMode(true);
      setEditOrderId(orderId);
      setLoadingEdit(true);
      api.get(`/orders/${orderId}`)
        .then((res) => {
          const order = res.data?.order;
          if (order) {
            populateFormFromOrder(order);
          }
        })
        .catch((err) => {
          console.error('Failed to load order for edit:', err);
          alert(err.response?.data?.error || 'Failed to load order for editing');
        })
        .finally(() => setLoadingEdit(false));
      return;
    }

    if (location.state?.editOrder) {
      const order = location.state.editOrder;
      setIsEditMode(true);
      setEditOrderId(String(order.id));
      populateFormFromOrder(order);
      return;
    }

    api.get('/orders/server-date').then((r) => {
      if (r.data?.serverDate) {
        setFormData((prev) => ({ ...prev, delivery_date: r.data.serverDate }));
      }
    }).catch(() => {});
  }, [editIdFromUrl, location.state?.editOrder, populateFormFromOrder]);

  function buildCustomerAddress() {
    const cityDisplay = formData.city === 'Other' ? (formData.city_other?.trim() || '') : (formData.city || 'Hyderabad');
    const parts = [
      formData.address_line1?.trim(),
      formData.address_line2?.trim(),
      cityDisplay,
      formData.state || 'Telangana',
      formData.pincode || '500001'
    ].filter(Boolean);
    return parts.join('\n');
  }

  const fetchAgents = async () => {
    try {
      const response = await api.get('/users/agents');
      setAgents(response.data.agents);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchCaterers = async () => {
    try {
      const response = await api.get('/caterers');
      setCaterers(response.data.caterers || []);
    } catch (error) {
      console.error('Error fetching caterers:', error);
      setCaterers([]);
    }
  };

  const fetchGrazingCatalog = async () => {
    try {
      const response = await api.get('/grazing/catalog');
      const cats = response.data.categories || [];
      setGrazingCatalog(cats);
      if (cats.length && !grazingPickCategoryId) {
        setGrazingPickCategoryId(String(cats[0].id));
      }
    } catch (error) {
      console.error('Error fetching grazing catalog:', error);
      setGrazingCatalog([]);
    }
  };

  const fetchDesserts = async () => {
    try {
      const response = await api.get('/users/desserts');
      setDesserts(response.data.desserts);
    } catch (error) {
      console.error('Error fetching desserts:', error);
    }
  };

  const fetchCupCakes = async () => {
    try {
      const response = await api.get('/users/cup-cakes');
      setCupCakes(response.data.cup_cakes || []);
    } catch (error) {
      console.error('Error fetching cup cakes:', error);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await api.get('/users/plants');
      setPlants(response.data.plants || []);
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const fetchFlavours = async () => {
    try {
      const response = await api.get('/users/flavours');
      setCakeFlavours(response.data.flavours || []);
    } catch (error) {
      console.error('Error fetching cake flavours:', error);
      setCakeFlavours([]);
    }
  };

  const addItem = (itemType) => {
    const newItem = {
      item_type: itemType,
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      special_instructions: ''
    };

    if (itemType === 'Cakes') {
      newItem.cake_details = {
        product_name: '',
        flavour: '',
        weight_kg: 1,
        weight_decimal: 0,
        price: 0,
        message_on_cake: '',
        message_on_card: '',
        message_on_base: '',
        special_instructions: ''
      };
      newItem.cake_images = [];
    } else if (itemType === 'Bouquets') {
      newItem.bouquet_images = [];
    } else if (itemType === 'Catering') {
      newItem.catering_files = [];
    } else if (itemType === 'Plants') {
      newItem.plant_images = [];
    } else if (itemType === 'Snack Boxes') {
      newItem.quantity = 1; // Overall quantity of snack boxes
      newItem.snack_box_items = [{ item_name: '', quantity: 1 }];
    } else if (itemType === 'Other Items') {
      newItem.custom_item_name = '';
      newItem.quantity = 1;
      newItem.unit_price = 0;
      newItem.total_price = 0;
    } else if (itemType === 'Desserts') {
      newItem.billed_as = 'per_piece';
      newItem.dessert_id = '';
    }

    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const isEventOrCaterer = ['Events', 'N Caterers', 'Caterers'].includes(formData.category);

  const addGrazingItemFromDraft = () => {
    const cat = grazingCatalog.find((c) => String(c.id) === String(grazingPickCategoryId));
    const picked = (cat?.items || []).find((it) => String(it.id) === String(grazingDraft.itemId));
    if (!picked) {
      alert('Select a grazing item');
      return;
    }
    const qty = parseFloat(grazingDraft.quantity) || 0;
    if (!(qty > 0)) {
      alert('Enter quantity greater than 0');
      return;
    }
    const newItem = {
      item_type: 'Grazing Table',
      grazing_item_id: picked.id,
      custom_item_name: picked.name,
      quantity: qty,
      unit_price: 0,
      total_price: 0,
      special_instructions: ''
    };
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
    setGrazingDraft({ itemId: '', quantity: 1, unit_price: '' });
  };

  const setupCost = (formData.items || []).reduce((sum, it) => sum + (parseFloat(it.total_price) || 0), 0);
  const travelExpenseNum = parseFloat(formData.travel_expense) || 0;
  const orderTotalWithTravel = setupCost + travelExpenseNum;
  const dessertItemsSum = (formData.items || [])
    .filter((it) => it.item_type === 'Desserts')
    .reduce((sum, it) => sum + (parseFloat(it.total_price) || 0), 0);
  const grazingItemsSum = (formData.items || [])
    .filter((it) => it.item_type === 'Grazing Table')
    .reduce((sum, it) => sum + (parseFloat(it.total_price) || 0), 0);

  useEffect(() => {
    const hasGrazing = (formData.items || []).some((it) => it.item_type === 'Grazing Table');
    if (!hasGrazing) setGrazingTableTotalInput('');
  }, [formData.items]);

  const distributeGrazingTotalOntoItems = (items, target) => {
    const grazingIdx = [];
    items.forEach((it, i) => {
      if (it.item_type === 'Grazing Table') grazingIdx.push(i);
    });
    if (!grazingIdx.length) return items;
    const next = items.map((it) => ({ ...it }));
    const qtySum = grazingIdx.reduce((s, i) => s + (parseFloat(next[i].quantity) || 0), 0);
    if (qtySum <= 0) {
      const i0 = grazingIdx[0];
      next[i0] = { ...next[i0], unit_price: target, total_price: target, quantity: 1 };
      return next;
    }
    let allocated = 0;
    grazingIdx.forEach((i, idx) => {
      const qty = parseFloat(next[i].quantity) || 0;
      const isLast = idx === grazingIdx.length - 1;
      const lineTotal = isLast
        ? Math.round((target - allocated) * 100) / 100
        : Math.round(((target * qty) / qtySum) * 100) / 100;
      allocated += lineTotal;
      next[i] = {
        ...next[i],
        total_price: lineTotal,
        unit_price: qty > 0 ? Math.round((lineTotal / qty) * 100) / 100 : lineTotal
      };
    });
    return next;
  };

  const applyGrazingTableTotal = () => {
    const target = parseFloat(grazingTableTotalInput);
    if (!Number.isFinite(target) || target <= 0) {
      alert('Enter a valid grazing table total greater than 0');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: distributeGrazingTotalOntoItems(prev.items, target)
    }));
    setGrazingTableTotalInput(String(Math.round(target * 100) / 100));
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const updateItemFields = (index, updates) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...updates };
      return { ...prev, items };
    });
  };

  const updateCakeDetails = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        cake_details: {
          ...items[index].cake_details,
          [field]: value
        }
      };
      return { ...prev, items };
    });
  };

  const updateCakeDetailsFields = (index, updates) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        cake_details: { ...items[index].cake_details, ...updates }
      };
      return { ...prev, items };
    });
  };

  /** Atomic cake price + line total (avoids stale setState overwriting total_price to 0). */
  const updateCakePrice = (index, price) => {
    const amount = parseFloat(price) || 0;
    setFormData((prev) => {
      const items = [...prev.items];
      const qty = items[index]?.quantity || 1;
      items[index] = {
        ...items[index],
        unit_price: amount,
        total_price: amount * qty,
        cake_details: {
          ...items[index].cake_details,
          price: amount
        }
      };
      return { ...prev, items };
    });
  };

  const addSnackBoxItem = (index) => {
    const items = [...formData.items];
    items[index].snack_box_items = [
      ...items[index].snack_box_items,
      { item_name: '', quantity: 1 }
    ];
    setFormData({ ...formData, items });
  };

  const updateSnackBoxItem = (itemIndex, snackIndex, field, value) => {
    const items = [...formData.items];
    items[itemIndex].snack_box_items[snackIndex][field] = value;
    setFormData({ ...formData, items });
  };

  const removeItem = (index) => {
    const items = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare form data - separate images from order data
      const orderData = { ...formData };
      orderData.customer_address = buildCustomerAddress();
      // Empty agent select is '' — send null so MySQL INT column does not 500
      orderData.agent_id =
        orderData.agent_id === '' || orderData.agent_id == null
          ? null
          : Number.parseInt(orderData.agent_id, 10) || null;

      const eventCats = ['Events', 'N Caterers', 'Caterers'].includes(orderData.category);
      if (eventCats) {
        if (!orderData.event_date || !orderData.event_venue?.trim() || !orderData.event_time) {
          alert('Event date, venue, and time are required for Events orders.');
          setLoading(false);
          return;
        }
        if (!orderData.caterer_select) {
          alert('Please select a caterer / party or choose Other.');
          setLoading(false);
          return;
        }
        if (orderData.caterer_select === '__other__') {
          if (!String(orderData.party_name || '').trim()) {
            alert('Please enter a party name.');
            setLoading(false);
            return;
          }
          orderData.caterer_id = null;
          orderData.party_name = String(orderData.party_name).trim();
        } else {
          orderData.caterer_id = Number.parseInt(orderData.caterer_select, 10) || null;
          const selectedCaterer = caterers.find((c) => String(c.id) === String(orderData.caterer_select));
          if (!orderData.party_name?.trim() && selectedCaterer) {
            orderData.party_name = selectedCaterer.name;
          }
        }
        // Customer section is hidden for Events — use party / venue for required order fields
        orderData.customer_name = String(orderData.party_name || '').trim() || 'Event order';
        const selectedCatererPhone = caterers.find((c) => String(c.id) === String(orderData.caterer_select))?.phone;
        orderData.customer_phone = orderData.customer_phone || selectedCatererPhone || '';
        orderData.customer_address = String(orderData.event_venue || '').trim();
        orderData.address_line1 = orderData.customer_address;
        // Sync delivery_date from event_date for Events
        if (!orderData.delivery_date || eventCats) {
          orderData.delivery_date = orderData.event_date;
        }
        if (!orderData.delivery_time) {
          orderData.delivery_time = orderData.event_time;
        }
        const hasGrazing = (orderData.items || []).some((it) => it.item_type === 'Grazing Table');
        const hasDessert = (orderData.items || []).some((it) => it.item_type === 'Desserts');
        if (!hasGrazing && !hasDessert) {
          alert('Add at least one dessert or grazing table item.');
          setLoading(false);
          return;
        }
        if (hasGrazing) {
          const tableTotal = parseFloat(grazingTableTotalInput);
          if (!(tableTotal > 0)) {
            alert('Enter the grazing table total after adding items.');
            setLoading(false);
            return;
          }
          orderData.items = distributeGrazingTotalOntoItems(orderData.items, tableTotal);
        }
        orderData.order_kind =
          hasGrazing && hasDessert ? 'mixed' : hasGrazing ? 'grazing' : 'standard';
        const te = parseFloat(orderData.travel_expense);
        orderData.travel_expense = Number.isFinite(te) && te >= 0 ? te : 0;
        // Online-only agent not required
        orderData.agent_id = null;
      } else {
        orderData.caterer_id = null;
        orderData.party_name = null;
        orderData.event_date = null;
        orderData.event_venue = null;
        orderData.event_time = null;
        orderData.travel_expense = 0;
        orderData.order_kind = 'standard';
      }
      delete orderData.caterer_select;

      // Advance may be 0 / empty — that is allowed. Order item total must be > 0.
      const advance = parseFloat(orderData.advance_payment);
      if (isNaN(advance) || advance < 0) {
        delete orderData.advance_payment;
      } else {
        // Allow 0 on edit to clear advance; on create 0 is omitted (no advance payment row)
        orderData.advance_payment = advance;
        if (!isEditMode && advance <= 0) delete orderData.advance_payment;
      }

      // Ensure cake line totals are wired from cake_details.price before validate/submit
      const normalizedItems = (orderData.items || []).map((it) => {
        if (it.item_type === 'Cakes') {
          const cakePrice = parseFloat(it.cake_details?.price);
          const qty = it.quantity || 1;
          if (Number.isFinite(cakePrice) && cakePrice > 0) {
            return {
              ...it,
              unit_price: cakePrice,
              total_price: cakePrice * qty
            };
          }
          return it;
        }
        if (it.item_type === 'Desserts' || it.item_type === 'Grazing Table') {
          const qty = parseFloat(it.quantity) || 0;
          const unit = parseFloat(it.unit_price) || 0;
          const lineTotal = parseFloat(it.total_price);
          return {
            ...it,
            total_price: Number.isFinite(lineTotal) && lineTotal > 0
              ? Math.round(lineTotal * 100) / 100
              : Math.round(qty * unit * 100) / 100,
            billed_as: it.item_type === 'Desserts' ? (it.billed_as || 'per_piece') : undefined,
            grazing_item_id: it.item_type === 'Grazing Table' ? (it.grazing_item_id || null) : undefined
          };
        }
        return it;
      });

      const itemsTotal = normalizedItems.reduce((sum, it) => sum + (parseFloat(it.total_price) || 0), 0);
      if (!(itemsTotal > 0)) {
        alert('Order cost must be greater than 0. Please enter a valid price. (Advance can be 0.)');
        setLoading(false);
        return;
      }
      const zeroCake = normalizedItems.some(
        (it) => it.item_type === 'Cakes' && !(parseFloat(it.cake_details?.price) > 0) && !(parseFloat(it.total_price) > 0)
      );
      if (zeroCake) {
        alert('Cake price cannot be 0. Please enter the cost of the cake.');
        setLoading(false);
        return;
      }
      const badOther = normalizedItems.find(
        (it) =>
          it.item_type === 'Other Items' &&
          (!String(it.custom_item_name || '').trim() || !(parseFloat(it.total_price) > 0))
      );
      if (badOther) {
        alert('Other Items need a name and a cost greater than 0.');
        setLoading(false);
        return;
      }
      const imagesToUpload = [];
      
      // Delivery charge is set by Store Manager in Order Details (Online Orders only).
      // At creation always 0; on edit do not send (preserve Store Manager's value).
      if (isEditMode) {
        delete orderData.delivery_charge;
      } else {
        orderData.delivery_charge = 0;
      }
      
      // Extract images from cake, bouquet, catering, and plant items (track index within each type for correct order_item_id match)
      let cakeTypeIndex = 0, bouquetTypeIndex = 0, cateringTypeIndex = 0, plantTypeIndex = 0;
      orderData.items = normalizedItems.map((item, index) => {
        let out = { ...item };
        if (item.item_type === 'Cakes') {
          if (item.cake_images) {
            const cakeImages = item.cake_images.filter(img => img.file);
            if (cakeImages.length > 0) {
              imagesToUpload.push({ typeIndex: cakeTypeIndex, images: cakeImages, fileType: 'cake_image' });
            }
            cakeTypeIndex++;
            const { cake_images, ...rest } = out;
            out = rest;
          } else {
            cakeTypeIndex++;
          }
          if (out.cake_details && out.cake_details.flavour === 'Others') {
            out.cake_details = { ...out.cake_details, flavour: out.cake_details.flavour_other || '' };
          }
        }
        if (item.item_type === 'Bouquets' && item.bouquet_images) {
          const bouquetImages = item.bouquet_images.filter(img => img.file);
          if (bouquetImages.length > 0) {
            imagesToUpload.push({ typeIndex: bouquetTypeIndex, images: bouquetImages, fileType: 'bouquet_reference' });
          }
          bouquetTypeIndex++;
          const { bouquet_images, ...rest } = out;
          out = rest;
        } else if (item.item_type === 'Bouquets') {
          bouquetTypeIndex++;
        }
        if (item.item_type === 'Catering' && item.catering_files) {
          const cateringFiles = item.catering_files.filter(f => f.file);
          if (cateringFiles.length > 0) {
            imagesToUpload.push({ typeIndex: cateringTypeIndex, images: cateringFiles, fileType: 'catering_pdf' });
          }
          cateringTypeIndex++;
          const { catering_files, ...rest } = out;
          out = rest;
        } else if (item.item_type === 'Catering') {
          cateringTypeIndex++;
        }
        if (item.item_type === 'Plants' && item.plant_images) {
          const plantImages = item.plant_images.filter(img => img.file);
          if (plantImages.length > 0) {
            imagesToUpload.push({ typeIndex: plantTypeIndex, images: plantImages, fileType: 'plant_reference' });
          }
          plantTypeIndex++;
          const { plant_images, ...rest } = out;
          out = rest;
        } else if (item.item_type === 'Plants') {
          plantTypeIndex++;
        }
        return out;
      });

      let orderId;
      if (isEditMode) {
        await api.put(`/orders/${editOrderId}`, orderData);
        orderId = editOrderId;
      } else {
        const response = await api.post('/orders', orderData);
        // Get order ID from response or fetch the order
        orderId = response.data.orderId;
        if (response.data.approval_status === 'awaiting') {
          alert(
            response.data.message ||
              'This Online Order is under ₹500 and was sent for Admin approval. It will appear in confirmed orders after approval.'
          );
        }
      }

      // Upload images after order is created
      if (imagesToUpload.length > 0 && orderId) {
        // Fetch order to get order item IDs
        const orderResponse = await api.get(`/orders/${orderId}`);
        const orderItems = orderResponse.data.order.items;
        
        // Upload images for each item (match by index within same type: typeIndex)
        for (const { typeIndex, images, fileType } of imagesToUpload) {
          const itemTypeFilter = fileType === 'bouquet_reference' ? 'Bouquets' : fileType === 'catering_pdf' ? 'Catering' : fileType === 'plant_reference' ? 'Plants' : 'Cakes';
          const filteredItems = orderItems.filter(oi => oi.item_type === itemTypeFilter);
          const orderItem = filteredItems[typeIndex];
          
          if (orderItem) {
            for (const image of images) {
              const uploadFormData = new FormData();
              uploadFormData.append('file', image.file);
              uploadFormData.append('fileType', fileType || 'cake_image');
              
              try {
                await api.post(`/upload/${orderItem.id}`, uploadFormData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
              } catch (error) {
                console.error('Error uploading image:', error);
              }
            }
          }
        }
      }

      navigate(`/orders/${orderId}`, isEditMode ? undefined : { state: { fromCreate: true } });
    } catch (error) {
      alert(error.response?.data?.error || `Error ${isEditMode ? 'updating' : 'creating'} order`);
    } finally {
      setLoading(false);
    }
  };

  const itemTypes = ['Cakes', 'Bouquets', 'Snack Boxes', 'Cup Cakes', 'Desserts', 'Plants', 'Catering', 'Other Items'];
  const grazingCategoryItems =
    (grazingCatalog.find((c) => String(c.id) === String(grazingPickCategoryId))?.items) || [];

  const canSetAdvance =
    ['Online Orders', 'Store-1', 'Store-2'].includes(formData.category) &&
    (['Order Creator', 'Admin', 'Franchise Admin'].includes(user?.role));

  const handleCategoryChange = (category) => {
    const next = { ...formData, category };
    if (['Events', 'N Caterers', 'Caterers'].includes(category)) {
      next.agent_id = '';
      next.order_kind = 'standard';
      next.items = [];
      setShowEventDesserts(false);
      setShowEventGrazing(false);
    } else {
      next.order_kind = 'standard';
      next.items = (next.items || []).filter((it) => it.item_type !== 'Grazing Table');
      setShowEventDesserts(false);
      setShowEventGrazing(false);
    }
    setFormData(next);
  };

  if (loadingEdit) {
    return (
      <div className="create-order">
        <PageNavbar />
        <div className="container">
          <div className="loading">Loading order for editing...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-order">
      <PageNavbar />
      <div className="container">
        <h1>{isEditMode ? 'Edit Order' : 'Create Order'}</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Order Details</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Category *</label>
                <select
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                >
                  {orderCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {!isEventOrCaterer && (
                <>
                  <div className="form-group">
                    <label>Delivery Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Delivery Time *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.delivery_time}
                      onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}
            </div>

            {isEventOrCaterer && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Caterer / Party *</label>
                    <select
                      className="form-control"
                      value={formData.caterer_select}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__other__') {
                          setFormData({ ...formData, caterer_select: v, caterer_id: '', party_name: formData.party_name || '' });
                        } else if (v) {
                          const c = caterers.find((x) => String(x.id) === String(v));
                          setFormData({
                            ...formData,
                            caterer_select: v,
                            caterer_id: v,
                            party_name: c?.name || ''
                          });
                        } else {
                          setFormData({ ...formData, caterer_select: '', caterer_id: '', party_name: '' });
                        }
                      }}
                      required
                    >
                      <option value="">Select caterer / party</option>
                      {caterers.map((c) => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                      <option value="__other__">Other (enter name)</option>
                    </select>
                  </div>
                  {formData.caterer_select === '__other__' && (
                    <div className="form-group">
                      <label>Party name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.party_name}
                        onChange={(e) => setFormData({ ...formData, party_name: e.target.value })}
                        required
                        placeholder="Party / event name"
                      />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Event date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.event_date}
                      onChange={(e) => setFormData({
                        ...formData,
                        event_date: e.target.value,
                        delivery_date: e.target.value || formData.delivery_date
                      })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Event venue *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.event_venue}
                      onChange={(e) => setFormData({ ...formData, event_venue: e.target.value })}
                      required
                      placeholder="Venue"
                    />
                  </div>
                  <div className="form-group">
                    <label>Event time *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.event_time}
                      onChange={(e) => setFormData({
                        ...formData,
                        event_time: e.target.value,
                        delivery_time: e.target.value || formData.delivery_time
                      })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Travel expense (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={formData.travel_expense}
                      onChange={(e) => setFormData({ ...formData, travel_expense: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {!isEventOrCaterer && (
          <div className="form-section">
            <h2>Customer Details</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Address Line 1 *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Street, building, area"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                className="form-control"
                placeholder="Landmark, floor, flat no."
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <select
                  className="form-control address-city-select"
                  value={formData.city === 'Other' ? 'Other' : (formData.city || 'Hyderabad')}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                >
                  {ADDRESS_CITY_OPTIONS.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>State</label>
                <select
                  className="form-control address-state-select"
                  value={formData.state || 'Telangana'}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                >
                  {ADDRESS_STATE_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Pincode</label>
                <select
                  className="form-control address-pincode-select"
                  value={formData.pincode || '500001'}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                >
                  {PINCODE_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData.city === 'Other' && (
              <div className="form-group">
                <label>City (other)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter city name"
                  value={formData.city_other || ''}
                  onChange={(e) => setFormData({ ...formData, city_other: e.target.value })}
                />
              </div>
            )}
          </div>
          )}

          {!isEventOrCaterer && (
            <div className="form-section">
              <h2>Delivery Details</h2>
              <div className="form-row">
                <div className="form-group">
                  <label>Agent</label>
                  <select
                    className="form-control"
                    value={formData.agent_id}
                    onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
                  >
                    <option value="">Select Agent</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {canSetAdvance && (
            <div className="form-section">
              <h2>Advance Payment (optional)</h2>
              <div className="form-row">
                <div className="form-group">
                  <label>Advance received for this order (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    placeholder="e.g., 500"
                    value={formData.advance_payment}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Allow empty or positive numbers only
                      if (v === '' || /^\d*\.?\d*$/.test(v)) {
                        setFormData({ ...formData, advance_payment: v });
                      }
                    }}
                  />
                  <small className="text-muted">
                    {isEditMode
                      ? 'Leave 0 or empty if no advance. Changing this updates the booking advance on the delivery challan.'
                      : 'Optional — leave empty or 0 if no advance was collected. Order item total must still be greater than 0.'}
                  </small>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h2>Order Items</h2>
            {isEventOrCaterer ? (
              <>
                <div className="add-items" style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <button
                    type="button"
                    className={`btn ${showEventDesserts ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => {
                      setShowEventDesserts(true);
                      if (!(formData.items || []).some((it) => it.item_type === 'Desserts')) {
                        addItem('Desserts');
                      }
                    }}
                  >
                    Add Desserts
                  </button>
                  <button
                    type="button"
                    className={`btn ${showEventGrazing ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setShowEventGrazing(true)}
                  >
                    Add Grazing Table
                  </button>
                </div>

                {showEventDesserts && (
                  <div className="event-panel event-panel--desserts" style={{ marginBottom: 16, padding: 12, border: '1px solid #e8dfd0', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>Desserts</h3>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => addItem('Desserts')}
                      >
                        + Add another dessert
                      </button>
                    </div>
                    {(formData.items || [])
                      .map((item, index) => ({ item, index }))
                      .filter(({ item }) => item.item_type === 'Desserts')
                      .map(({ item, index }) => (
                        <ItemForm
                          key={`dessert-${index}`}
                          item={item}
                          index={index}
                          desserts={desserts}
                          cupCakes={cupCakes}
                          plants={plants}
                          formData={formData}
                          setFormData={setFormData}
                          updateItem={updateItem}
                          updateItemFields={updateItemFields}
                          updateCakeDetails={updateCakeDetails}
                          updateCakeDetailsFields={updateCakeDetailsFields}
                          updateCakePrice={updateCakePrice}
                          addSnackBoxItem={addSnackBoxItem}
                          updateSnackBoxItem={updateSnackBoxItem}
                          removeItem={removeItem}
                          cakeFlavours={cakeFlavours}
                          showBilledAs
                        />
                      ))}
                  </div>
                )}

                {showEventGrazing && (
                  <div className="grazing-picker event-panel event-panel--grazing" style={{ marginBottom: 16, padding: 12, border: '1px solid #e8dfd0', borderRadius: 8 }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: '1rem' }}>Grazing table</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Grazing category</label>
                        <select
                          className="form-control"
                          value={grazingPickCategoryId}
                          onChange={(e) => {
                            setGrazingPickCategoryId(e.target.value);
                            setGrazingDraft({ itemId: '', quantity: 1, unit_price: '' });
                          }}
                        >
                          <option value="">Select category</option>
                          {grazingCatalog.map((c) => (
                            <option key={c.id} value={String(c.id)}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Item</label>
                        <select
                          className="form-control"
                          value={grazingDraft.itemId}
                          onChange={(e) => {
                            const id = e.target.value;
                            setGrazingDraft({
                              ...grazingDraft,
                              itemId: id,
                              unit_price: ''
                            });
                          }}
                        >
                          <option value="">Select item</option>
                          {grazingCategoryItems.map((it) => (
                            <option key={it.id} value={String(it.id)}>
                              {it.name}{it.unit ? ` (${it.unit})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Qty</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          className="form-control"
                          value={grazingDraft.quantity}
                          onChange={(e) => setGrazingDraft({ ...grazingDraft, quantity: e.target.value })}
                        />
                      </div>
                    </div>
                    <button type="button" className="btn btn-secondary" onClick={addGrazingItemFromDraft}>
                      Add grazing item
                    </button>
                    {(formData.items || [])
                      .map((item, index) => ({ item, index }))
                      .filter(({ item }) => item.item_type === 'Grazing Table')
                      .map(({ item, index }) => (
                        <ItemForm
                          key={`grazing-${index}`}
                          item={item}
                          index={index}
                          desserts={desserts}
                          cupCakes={cupCakes}
                          plants={plants}
                          formData={formData}
                          setFormData={setFormData}
                          updateItem={updateItem}
                          updateItemFields={updateItemFields}
                          updateCakeDetails={updateCakeDetails}
                          updateCakeDetailsFields={updateCakeDetailsFields}
                          updateCakePrice={updateCakePrice}
                          addSnackBoxItem={addSnackBoxItem}
                          updateSnackBoxItem={updateSnackBoxItem}
                          removeItem={removeItem}
                          cakeFlavours={cakeFlavours}
                          showBilledAs={false}
                        />
                      ))}
                    {(formData.items || []).some((it) => it.item_type === 'Grazing Table') && (
                      <div className="grazing-table-total">
                        <div>
                          <label htmlFor="grazing-table-total-input">Grazing table total (₹)</label>
                          <input
                            id="grazing-table-total-input"
                            type="number"
                            min="0.01"
                            step="0.01"
                            className="form-control"
                            value={grazingTableTotalInput}
                            onChange={(e) => setGrazingTableTotalInput(e.target.value)}
                            onBlur={applyGrazingTableTotal}
                            required
                          />
                        </div>
                        <p className="grazing-table-total__hint">
                          Add all grazing items first, then enter the full table value. Line prices are not entered per item.
                          {grazingItemsSum > 0 ? ` Applied: ₹${grazingItemsSum.toFixed(2)}` : ''}
                        </p>
                        <button type="button" className="btn btn-primary" onClick={applyGrazingTableTotal}>
                          Apply table total
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!showEventDesserts && !showEventGrazing && (
                  <p className="text-muted" style={{ marginTop: 4 }}>
                    Choose Add Desserts or Add Grazing Table to start adding items.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="add-items">
                  {itemTypes.map(type => (
                    <button
                      key={type}
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => addItem(type)}
                    >
                      Add {type}
                    </button>
                  ))}
                </div>

                {formData.items.map((item, index) => (
                  <ItemForm
                    key={index}
                    item={item}
                    index={index}
                    desserts={desserts}
                    cupCakes={cupCakes}
                    plants={plants}
                    formData={formData}
                    setFormData={setFormData}
                    updateItem={updateItem}
                    updateItemFields={updateItemFields}
                    updateCakeDetails={updateCakeDetails}
                    updateCakeDetailsFields={updateCakeDetailsFields}
                    updateCakePrice={updateCakePrice}
                    addSnackBoxItem={addSnackBoxItem}
                    updateSnackBoxItem={updateSnackBoxItem}
                    removeItem={removeItem}
                    cakeFlavours={cakeFlavours}
                    showBilledAs={false}
                  />
                ))}
              </>
            )}

            {isEventOrCaterer && (
              <div className="event-order-totals">
                <div className="event-order-total-row">
                  <span>Desserts</span>
                  <strong>₹{dessertItemsSum.toFixed(2)}</strong>
                </div>
                <div className="event-order-total-row">
                  <span>Grazing table</span>
                  <strong>₹{grazingItemsSum.toFixed(2)}</strong>
                </div>
                <div className="event-order-total-row">
                  <span>Travel expense</span>
                  <strong>₹{travelExpenseNum.toFixed(2)}</strong>
                </div>
                <div className="event-order-total-row event-order-total-row--grand">
                  <span>Order total</span>
                  <strong>₹{orderTotalWithTravel.toFixed(2)}</strong>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/orders')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || formData.items.length === 0}>
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Order' : 'Create Order')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FLAVOUR_OTHERS = 'Others';

const dessertLabel = (d) =>
  `${d.name}${d.default_price != null ? ` (₹${d.default_price})` : ''}`;

/** Type-to-search replacement for a long native select of desserts */
function SearchableDessertPicker({ desserts, dessertId, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => (desserts || []).find((d) => String(d.id) === String(dessertId)),
    [desserts, dessertId]
  );

  useEffect(() => {
    const sel = (desserts || []).find((d) => String(d.id) === String(dessertId));
    if (sel) setQuery(dessertLabel(sel));
    else setQuery('');
  }, [dessertId, desserts]);

  const filtered = useMemo(() => {
    const list = desserts || [];
    const q = query.trim().toLowerCase();
    let out = !q
      ? list
      : list.filter((d) => d.name.toLowerCase().includes(q));
    if (selected && !out.some((d) => String(d.id) === String(selected.id))) {
      out = [selected, ...out];
    }
    return out;
  }, [desserts, query, selected]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="searchable-list-picker" ref={wrapRef}>
      <input
        type="text"
        className="form-control dessert-select"
        placeholder="Search desserts by name…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          if (selected) setQuery(dessertLabel(selected));
          else setQuery('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        autoComplete="off"
        spellCheck="false"
      />
      {open && (
        <ul className="searchable-list-picker-dropdown" role="listbox">
          {filtered.length === 0 ? (
            <li className="searchable-list-picker-empty">No matches</li>
          ) : (
            filtered.map((d) => (
              <li
                key={d.id}
                role="option"
                aria-selected={String(dessertId) === String(d.id)}
                className={String(dessertId) === String(d.id) ? 'is-active' : ''}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(d);
                  setQuery(dessertLabel(d));
                  setOpen(false);
                }}
              >
                {dessertLabel(d)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

const ItemForm = ({
  item,
  index,
  desserts,
  cupCakes,
  plants,
  formData,
  setFormData,
  updateItem,
  updateItemFields,
  updateCakeDetails,
  updateCakeDetailsFields,
  updateCakePrice,
  addSnackBoxItem,
  updateSnackBoxItem,
  removeItem,
  cakeFlavours,
  showBilledAs
}) => {
  /** Persisted files carry `id`; removing calls DELETE /upload/:id so edits keep adds/removes correctly. */
  const removeAttachment = async (field, imgIndex) => {
    const list = [...(formData.items[index][field] || [])];
    const removed = list[imgIndex];
    if (removed?.id) {
      try {
        await api.delete(`/upload/${removed.id}`);
      } catch (err) {
        alert(err.response?.data?.error || 'Could not remove file');
        return;
      }
    }
    if (removed?.preview && String(removed.preview).startsWith('blob:')) {
      URL.revokeObjectURL(removed.preview);
    }
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: list.filter((_, i) => i !== imgIndex)
    };
    setFormData({ ...formData, items: newItems });
  };

  return (
    <div className="item-form">
      <div className="item-header">
        <h3>{item.item_type}</h3>
        <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(index)}>
          Remove
        </button>
      </div>

      {item.item_type === 'Cakes' && (
        <div className="cake-form">
          <div className="form-group">
            <label>Product Name (Cake Name)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., Chocolate Truffle Cake, Red Velvet Cake"
              value={item.cake_details?.product_name || ''}
              onChange={(e) => updateCakeDetails(index, 'product_name', e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group flavour-group">
              <label>Flavour</label>
              <input
                type="text"
                className="form-control flavour-input"
                list={`flavour-list-${index}`}
                placeholder="Type 3–4 letters to search or select from list"
                value={item.cake_details?.flavour === FLAVOUR_OTHERS ? FLAVOUR_OTHERS : (item.cake_details?.flavour || '')}
                onChange={(e) => {
                  updateCakeDetails(index, 'flavour', e.target.value);
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (!v) return;
                  const match = cakeFlavours.find(f => f.name.toUpperCase() === v.toUpperCase());
                  if (match) {
                    updateCakeDetails(index, 'flavour', match.name);
                  } else if (v.toUpperCase() === FLAVOUR_OTHERS.toUpperCase()) {
                    updateCakeDetailsFields(index, { flavour: FLAVOUR_OTHERS });
                  } else {
                    updateCakeDetailsFields(index, { flavour: FLAVOUR_OTHERS, flavour_other: v });
                  }
                }}
              />
              <datalist id={`flavour-list-${index}`}>
                {cakeFlavours.map(f => (
                  <option key={f.id} value={f.name} />
                ))}
                <option value={FLAVOUR_OTHERS} />
              </datalist>
              {(item.cake_details?.flavour === FLAVOUR_OTHERS || (item.cake_details?.flavour && !cakeFlavours.some(f => f.name === item.cake_details?.flavour))) && (
                <div className="form-group flavour-other-wrap">
                  <label>Specify flavour (other)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter flavour name"
                    value={item.cake_details?.flavour === FLAVOUR_OTHERS ? (item.cake_details?.flavour_other || '') : (item.cake_details?.flavour || '')}
                    onChange={(e) => updateCakeDetailsFields(index, { flavour_other: e.target.value, flavour: FLAVOUR_OTHERS })}
                  />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Weight (KG)</label>
              <select
                className="form-control"
                value={item.cake_details?.weight_kg != null ? item.cake_details.weight_kg : 1}
                onChange={(e) => updateCakeDetails(index, 'weight_kg', parseInt(e.target.value, 10))}
              >
                {Array.from({ length: 31 }, (_, i) => i).map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Grams</label>
              <select
                className="form-control"
                value={String(item.cake_details?.weight_decimal ?? 0)}
                onChange={(e) => updateCakeDetails(index, 'weight_decimal', parseFloat(e.target.value))}
              >
                <option value="0">0 g</option>
                <option value="0.25">250 g</option>
                <option value="0.5">500 g</option>
                <option value="0.75">750 g</option>
              </select>
            </div>
            <div className="form-group">
              <label>Price (₹)</label>
              <input
                type="number"
                className="form-control"
                value={item.cake_details?.price || ''}
                onChange={(e) => updateCakePrice(index, e.target.value)}
                min="0.01"
                step="0.01"
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Message on Cake</label>
            <textarea
              className="form-control"
              rows={2}
              inputMode="text"
              autoComplete="off"
              value={item.cake_details?.message_on_cake || ''}
              onChange={(e) => updateCakeDetails(index, 'message_on_cake', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Message on Card</label>
            <textarea
              className="form-control"
              rows={2}
              inputMode="text"
              autoComplete="off"
              value={item.cake_details?.message_on_card || ''}
              onChange={(e) => updateCakeDetails(index, 'message_on_card', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Message on Base</label>
            <textarea
              className="form-control"
              rows={2}
              inputMode="text"
              autoComplete="off"
              value={item.cake_details?.message_on_base || ''}
              onChange={(e) => updateCakeDetails(index, 'message_on_base', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Cake Images (up to 20)</label>
            <small className="text-muted" style={{ display: 'block', marginBottom: 6 }}>
              Add more with the file picker; Remove deletes an image from this order (saved immediately for existing images).
            </small>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (files.length + (item.cake_images?.length || 0) > 20) {
                  alert('Maximum 20 images allowed');
                  e.target.value = '';
                  return;
                }

                const uploadedImages = [];
                for (const file of files) {
                  try {
                    uploadedImages.push({
                      file: file,
                      preview: URL.createObjectURL(file)
                    });
                  } catch (error) {
                    console.error('Error preparing image:', error);
                  }
                }

                const items = [...formData.items];
                items[index].cake_images = [...(items[index].cake_images || []), ...uploadedImages];
                setFormData({ ...formData, items });
                e.target.value = '';
              }}
            />
            {item.cake_images && item.cake_images.length > 0 && (
              <div className="image-preview-grid">
                {item.cake_images.map((img, imgIndex) => (
                  <div key={img.id || img.preview || `cake-${imgIndex}`} className="image-preview">
                    <img 
                      src={img.preview || getImageUrl(img.file_path) || getImageUrl(`/uploads/${img.file_name}`)} 
                      alt={`Cake ${imgIndex + 1}`}
                      style={{ width: '100px', height: '100px', objectFit: 'cover', margin: '5px' }}
                      onError={(e) => {
                        console.error('Image load error:', img);
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeAttachment('cake_images', imgIndex)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {item.item_type === 'Snack Boxes' && (
        <div className="snack-box-form">
          <div className="form-group">
            <label>Number of Snack Boxes *</label>
            <input
              type="number"
              className="form-control"
              value={item.quantity || 1}
              onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
              min="1"
              required
            />
            <small className="text-muted">Total number of snack boxes to prepare</small>
          </div>
          <div className="form-group">
            <label>Total Price (₹) *</label>
            <input
              type="number"
              className="form-control"
              value={item.total_price ?? ''}
              onChange={(e) => {
                const total = parseFloat(e.target.value) || 0;
                const qty = item.quantity || 1;
                updateItemFields(index, {
                  total_price: total,
                  unit_price: qty > 0 ? Math.round((total / qty) * 100) / 100 : total
                });
              }}
              min="0.01"
              step="0.01"
              required
              placeholder="Enter snack box total"
            />
          </div>
          <div className="form-group">
            <label>Items in Each Snack Box</label>
            {item.snack_box_items?.map((snackItem, snackIndex) => (
              <div key={snackIndex} className="snack-item-row">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Item name (e.g., Chocolate, Cookie)"
                  value={snackItem.item_name}
                  onChange={(e) => updateSnackBoxItem(index, snackIndex, 'item_name', e.target.value)}
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Quantity per box"
                  value={snackItem.quantity}
                  onChange={(e) => updateSnackBoxItem(index, snackIndex, 'quantity', parseInt(e.target.value) || 1)}
                  min="1"
                />
                {item.snack_box_items.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      const items = [...formData.items];
                      items[index].snack_box_items = items[index].snack_box_items.filter((_, i) => i !== snackIndex);
                      setFormData({ ...formData, items });
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => addSnackBoxItem(index)}
            >
              Add Item to Box
            </button>
          </div>
        </div>
      )}

      {item.item_type === 'Desserts' && (
        <div className="dessert-fields">
          <div className="form-row">
            <div className="form-group">
              <label>Dessert</label>
              <SearchableDessertPicker
                desserts={desserts}
                dessertId={item.dessert_id}
                onPick={(picked) => {
                  const qty = item.quantity || 1;
                  const unitPrice =
                    picked && picked.default_price != null
                      ? parseFloat(picked.default_price)
                      : item.unit_price || 0;
                  updateItemFields(index, {
                    dessert_id: String(picked.id),
                    unit_price: unitPrice,
                    total_price: unitPrice * qty,
                    billed_as: item.billed_as || 'per_piece'
                  });
                }}
              />
            </div>
            {showBilledAs && (
              <div className="form-group">
                <label>Billed as</label>
                <select
                  className="form-control"
                  value={item.billed_as || 'per_piece'}
                  onChange={(e) => updateItem(index, 'billed_as', e.target.value)}
                >
                  <option value="per_piece">Per piece</option>
                  <option value="per_kg">Per kg</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                min="1"
                step={item.billed_as === 'per_kg' ? '0.01' : '1'}
                className="form-control"
                value={item.quantity ?? 1}
                onChange={(e) => {
                  const qty = parseFloat(e.target.value) || 1;
                  const unitPrice = parseFloat(item.unit_price) || 0;
                  updateItemFields(index, { quantity: qty, total_price: unitPrice * qty });
                }}
              />
            </div>
            <div className="form-group">
              <label>Price per Unit (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control dessert-amount-input"
                value={item.unit_price ?? ''}
                onChange={(e) => {
                  const unitPrice = parseFloat(e.target.value) || 0;
                  const qty = item.quantity || 1;
                  updateItemFields(index, { unit_price: unitPrice, total_price: unitPrice * qty });
                }}
              />
            </div>
            <div className="form-group dessert-total">
              <label>Total Amount (₹)</label>
              <div className="dessert-total-value">₹{(parseFloat(item.total_price) || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {item.item_type === 'Grazing Table' && (
        <div className="dessert-fields">
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label>Item</label>
              <input
                type="text"
                className="form-control"
                value={item.custom_item_name || ''}
                readOnly
              />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="form-control"
                value={item.quantity ?? 1}
                onChange={(e) => {
                  const qty = parseFloat(e.target.value) || 0;
                  updateItemFields(index, {
                    quantity: qty,
                    total_price: 0,
                    unit_price: 0
                  });
                }}
              />
            </div>
          </div>
          <p className="text-muted" style={{ margin: '6px 0 0', fontSize: '0.85rem' }}>
            Pricing is set via Grazing table total below — not per item.
          </p>
        </div>
      )}

      {item.item_type === 'Cup Cakes' && (
        <div className="dessert-fields">
          <div className="form-row">
            <div className="form-group">
              <label>Cup Cake</label>
              <select
                className="form-control dessert-select"
                value={String(item.cup_cake_id ?? '')}
                onChange={(e) => {
                  const id = e.target.value;
                  const selected = (cupCakes || []).find(c => String(c.id) === String(id));
                  const qty = item.quantity || 1;
                  const unitPrice = selected && selected.default_price != null ? parseFloat(selected.default_price) : (item.unit_price || 0);
                  updateItemFields(index, { cup_cake_id: id, unit_price: unitPrice, total_price: unitPrice * qty });
                }}
              >
                <option value="">Select Cup Cake</option>
                {(cupCakes || []).map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}{c.default_price != null ? ` (₹${c.default_price})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" min="1" className="form-control" value={item.quantity ?? 1} onChange={(e) => { const qty = parseInt(e.target.value, 10) || 1; const unitPrice = parseFloat(item.unit_price) || 0; updateItemFields(index, { quantity: qty, total_price: unitPrice * qty }); }} />
            </div>
            <div className="form-group">
              <label>Price per Unit (₹)</label>
              <input type="number" min="0" step="0.01" className="form-control dessert-amount-input" value={item.unit_price ?? ''} onChange={(e) => { const unitPrice = parseFloat(e.target.value) || 0; const qty = item.quantity || 1; updateItemFields(index, { unit_price: unitPrice, total_price: unitPrice * qty }); }} />
            </div>
            <div className="form-group dessert-total">
              <label>Total (₹)</label>
              <div className="dessert-total-value">₹{(parseFloat(item.total_price) || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {item.item_type === 'Plants' && (
        <div className="dessert-fields">
          <div className="form-row">
            <div className="form-group">
              <label>Plant</label>
              <select
                className="form-control dessert-select"
                value={String(item.plant_id ?? '')}
                onChange={(e) => {
                  const id = e.target.value;
                  updateItem(index, 'plant_id', id);
                  const selected = (plants || []).find(p => String(p.id) === String(id));
                  const qty = item.quantity || 1;
                  const unitPrice = selected && selected.default_price != null ? parseFloat(selected.default_price) : (item.unit_price || 0);
                  updateItemFields(index, { unit_price: unitPrice, total_price: unitPrice * qty });
                }}
              >
                <option value="">Select Plant</option>
                {(plants || []).map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}{p.default_price != null ? ` (₹${p.default_price})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" min="1" className="form-control" value={item.quantity ?? 1} onChange={(e) => { const qty = parseInt(e.target.value, 10) || 1; const unitPrice = parseFloat(item.unit_price) || 0; updateItemFields(index, { quantity: qty, total_price: unitPrice * qty }); }} />
            </div>
            <div className="form-group">
              <label>Price per Unit (₹)</label>
              <input type="number" min="0" step="0.01" className="form-control dessert-amount-input" value={item.unit_price ?? ''} onChange={(e) => { const unitPrice = parseFloat(e.target.value) || 0; const qty = item.quantity || 1; updateItemFields(index, { unit_price: unitPrice, total_price: unitPrice * qty }); }} />
            </div>
            <div className="form-group dessert-total">
              <label>Total (₹)</label>
              <div className="dessert-total-value">₹{(parseFloat(item.total_price) || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="form-group">
            <label>Plant Reference Images (optional, up to 5)</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const current = item.plant_images?.length || 0;
                  if (files.length + current > 5) {
                    alert('Maximum 5 images allowed');
                    e.target.value = '';
                    return;
                  }
                  const uploadedImages = files.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }));
                  const items = [...formData.items];
                  items[index].plant_images = [...(items[index].plant_images || []), ...uploadedImages];
                  setFormData({ ...formData, items });
                  e.target.value = '';
                }}
              />
            {item.plant_images && item.plant_images.length > 0 && (
              <div className="image-preview-grid" style={{ marginTop: 8 }}>
                {item.plant_images.map((img, imgIndex) => (
                  <div key={img.id || img.preview || `plant-${imgIndex}`} className="image-preview">
                    <img
                      src={img.preview || getImageUrl(img.file_path) || getImageUrl('/uploads/' + (img.file_name || ''))}
                      alt={'Plant ' + (imgIndex + 1)}
                      style={{ width: 80, height: 80, objectFit: 'cover', margin: 4 }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      style={{ marginTop: 4, fontSize: 11 }}
                      onClick={() => removeAttachment('plant_images', imgIndex)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {(item.item_type === 'Bouquets' || item.item_type === 'Catering') && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" className="form-control" value={item.quantity || 1} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value))} />
            </div>
            <div className="form-group">
              <label>Total Price (₹)</label>
              <input type="number" className="form-control" value={item.total_price || 0} onChange={(e) => updateItem(index, 'total_price', parseFloat(e.target.value))} />
            </div>
          </div>
          {item.item_type === 'Bouquets' && (
            <div className="form-group">
              <label>Bouquet Images (optional, up to 5)</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const current = item.bouquet_images?.length || 0;
                  if (files.length + current > 5) {
                    alert('Maximum 5 images allowed');
                    e.target.value = '';
                    return;
                  }
                  const uploadedImages = files.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }));
                  const items = [...formData.items];
                  items[index].bouquet_images = [...(items[index].bouquet_images || []), ...uploadedImages];
                  setFormData({ ...formData, items });
                  e.target.value = '';
                }}
              />
              {item.bouquet_images && item.bouquet_images.length > 0 && (
                <div className="image-preview-grid" style={{ marginTop: 8 }}>
                  {item.bouquet_images.map((img, imgIndex) => (
                    <div key={img.id || img.preview || `bouquet-${imgIndex}`} className="image-preview">
                      <img
                        src={img.preview || getImageUrl(img.file_path) || getImageUrl('/uploads/' + (img.file_name || ''))}
                        alt={'Bouquet ' + (imgIndex + 1)}
                        style={{ width: 80, height: 80, objectFit: 'cover', margin: 4 }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        style={{ marginTop: 4, fontSize: 11 }}
                        onClick={() => removeAttachment('bouquet_images', imgIndex)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {item.item_type === 'Catering' && (
            <div className="form-group">
              <label>Menu Files (Images, PDF, or Word documents - up to 5)</label>
              <input
                type="file"
                className="form-control"
                accept="image/*,.pdf,.doc,.docx"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const current = item.catering_files?.length || 0;
                  if (files.length + current > 5) {
                    alert('Maximum 5 files allowed');
                    e.target.value = '';
                    return;
                  }
                  const uploadedFiles = files.map(file => ({
                    file,
                    file_name: file.name,
                    file_type: file.type
                  }));
                  const items = [...formData.items];
                  items[index].catering_files = [...(items[index].catering_files || []), ...uploadedFiles];
                  setFormData({ ...formData, items });
                  e.target.value = '';
                }}
              />
              {item.catering_files && item.catering_files.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {item.catering_files.map((file, fileIndex) => (
                    <div key={file.id || file.file_name || fileIndex} style={{ padding: '8px', border: '1px solid #ddd', marginBottom: '4px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        📄 {file.file_name || file.file?.name}
                        {file.file_path && (
                          <a href={getImageUrl(file.file_path)} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '8px', fontSize: '12px' }}>
                            View
                          </a>
                        )}
                      </span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeAttachment('catering_files', fileIndex)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {item.item_type === 'Other Items' && (
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Item name</label>
            <input
              type="text"
              className="form-control"
              value={item.custom_item_name || ''}
              onChange={(e) => updateItem(index, 'custom_item_name', e.target.value)}
              placeholder="e.g. Candles, Knife, Topper"
              required
            />
          </div>
          <div className="form-group">
            <label>Quantity</label>
            <input
              type="number"
              className="form-control"
              min="0.01"
              step="0.01"
              value={item.quantity || 1}
              onChange={(e) => {
                const qty = parseFloat(e.target.value) || 0;
                const unit = parseFloat(item.unit_price) || 0;
                updateItemFields(index, {
                  quantity: qty,
                  total_price: Math.round(qty * unit * 100) / 100
                });
              }}
              required
            />
          </div>
          <div className="form-group">
            <label>Cost (₹)</label>
            <input
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              value={item.unit_price || 0}
              onChange={(e) => {
                const unit = parseFloat(e.target.value) || 0;
                const qty = parseFloat(item.quantity) || 1;
                updateItemFields(index, {
                  unit_price: unit,
                  total_price: Math.round(qty * unit * 100) / 100
                });
              }}
              required
            />
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Special Instructions</label>
        <textarea
          className="form-control"
          rows="2"
          value={item.special_instructions || ''}
          onChange={(e) => updateItem(index, 'special_instructions', e.target.value)}
        />
      </div>
    </div>
  );
};

export default CreateOrder;
